/**
 * Feeding the friction and rubber boards from the platform.
 *
 * Renders nothing and sits in the shell. On the platform, once the boards'
 * engine is here, it reads the site's surveys and manual tests, the finished
 * rubber removals in the work-order register, the filed operator's returns
 * and the planner's saved figures, and hands each to the boards
 * (`mergeFriction.ts`), replacing the sample's.
 *
 * Careful, as the other feeds are:
 *
 *   - It never causes the engine to load, and asks nothing until it has
 *     (`enabled`), so a page with no board makes no extra request.
 *   - The merge, and the tables it reads, are imported only when there is
 *     something to merge — never into the entry chunk.
 *   - Under the sample it does nothing: the seeds stand.
 *   - A refused read (403) is an answer: the board keeps only this browser's
 *     own entries and the shell says why, rather than showing the sample or
 *     claiming the register is empty. Any other failure leaves what is shown.
 */
import { useEffect, useState } from "react";
import { useSession } from "../../../app/SessionContext";
import { RUBBER_MODEL, useFrictionSurveys, useFrictionTestRecords, useModelParams } from "../../../api/frictionQueries";
import { useWorkOrders } from "../../../api/queries";
import { demoIfLoaded, whenDemoLoaded } from "../../domain/load";
import { clearFrictionReport } from "../frictionReport";

const merger = () => import("../mergeFriction");

/** A read the platform refused, in its own words; undefined for anything else. */
export function refusal(e: unknown): string | undefined {
  const status = (e as { status?: number } | null)?.status;
  if (status !== 403) return undefined;
  return e instanceof Error ? e.message : String(e);
}

export function FrictionFeed() {
  const { source } = useSession();
  const live = source.kind === "platform";
  const [engine, setEngine] = useState(() => demoIfLoaded() !== null);
  useEffect(() => {
    if (!engine) whenDemoLoaded(() => setEngine(true));
  }, [engine]);
  const on = live && engine;

  const surveys = useFrictionSurveys({ enabled: on });
  const returns = useFrictionTestRecords({ enabled: on });
  const model = useModelParams(RUBBER_MODEL, { enabled: on });
  /* the same register read the work-order boards share */
  const orders = useWorkOrders();

  const sv = surveys.data;
  const svWhy = refusal(surveys.error);
  const rt = returns.data;
  const rtWhy = refusal(returns.error);
  const mp = model.data;
  const wo = orders.data?.items;
  const woWhy = refusal(orders.error);

  useEffect(() => {
    if (!live) clearFrictionReport();
  }, [live]);

  useEffect(() => {
    if (!on) return;
    if (sv) whenDemoLoaded(() => void merger().then((m) => m.mergeSurveys(sv)));
    else if (svWhy) whenDemoLoaded(() => void merger().then((m) => m.mergeSurveys([], svWhy)));
  }, [on, sv, svWhy]);

  useEffect(() => {
    if (!on) return;
    if (wo) whenDemoLoaded(() => void merger().then((m) => m.mergeRemovals(wo)));
    else if (woWhy) whenDemoLoaded(() => void merger().then((m) => m.mergeRemovals([], woWhy)));
  }, [on, wo, woWhy]);

  useEffect(() => {
    if (!on) return;
    if (rt) whenDemoLoaded(() => void merger().then((m) => m.mergeReturns(rt)));
    else if (rtWhy) whenDemoLoaded(() => void merger().then((m) => m.mergeReturns([], rtWhy)));
  }, [on, rt, rtWhy]);

  useEffect(() => {
    if (!on || !mp) return;
    whenDemoLoaded(() => void merger().then((m) => m.mergeModel(mp)));
  }, [on, mp]);

  return null;
}
